import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import fetchWrapper from '../utils/httpClient';
import { API_GET_ALL_USERS, API_DELETE_USER, API_UPDATE_USER } from '../utils/apis';
// Asynchronous thunk for fetching the list of all users
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ page = 0, size = 10, login = '', roleId = '' } = {}) => {
    try {
      let url = `${API_GET_ALL_USERS}?page=${page}&size=${size}`;
      if (login) url += `&login=${encodeURIComponent(login)}`;
      if (roleId) url += `&roleId=${encodeURIComponent(roleId)}`;

      const response = await fetchWrapper.get(url);
      return {
        data: response.resdata.content,
        total: response.resdata.totalElements,
        page: response.resdata.pageable.pageNumber,
        size: response.resdata.pageable.pageSize
      };
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Asynchronous thunk for deleting a user
export const deleteUser = createAsyncThunk('users/deleteUser', async (userId) => {
  try {
    const response = await fetchWrapper.delete(`${API_DELETE_USER}?userId=${userId}`);
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
});

// Asynchronous thunk for updating user information
export const updateUser = createAsyncThunk('users/updateUser', async (payload) => {
  try {
    const response = await fetchWrapper.put(API_UPDATE_USER, payload);
    return response;
  } catch (error) {
    throw new Error(error.message);
  }
});

// Asynchronous thunk for fetching detailed information of a single user
export const fetchUserDetails = createAsyncThunk(
  'users/fetchUserDetails',
  async (userId) => {
    try {
      const response = await fetchWrapper.get(`/cmsadmin/api/users/${userId}`);
      return response;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Create the userSlice
const userSlice = createSlice({
  name: 'users',
  initialState: {
    data: [],
    loading: false,
    error: null,
    selectedUser: null,
    pagination: {
      page: 0,
      size: 10,
      total: 0
    },
    status: 'idle'
  },
  extraReducers: (builder) => {
    builder
      // Handle different states of fetchUsers
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.pagination = {
          page: action.payload.page,
          size: action.payload.size,
          total: action.payload.total
        };
        state.status = 'succeeded';
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'failed';
      })

      // Handle different states of deleteUser
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.loading = false;
        state.status = 'succeeded';
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'failed';
      })

      // Handle different states of updateUser
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.loading = false;
        state.status = 'succeeded';
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'failed';
      })

      // Handle different states of fetchUserDetails
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload.resdata;
        state.status = 'succeeded';
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.status = 'failed';
      });
  }
});

export default userSlice.reducer;    